import React, { useState, useEffect } from 'react';
import { MissingItem } from '../../types/missingItem';
import { missingItemService } from '../../services/missingItemService';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import MissingItemUploadModal from './MissingItemUploadModal';
import toast from 'react-hot-toast';
import {
  CheckCircleIcon,
  ClockIcon,
  DocumentArrowUpIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';

interface MissingItemsListProps {
  customerId?: string;
  isCustomerView?: boolean;
}

const MissingItemsList: React.FC<MissingItemsListProps> = ({ customerId, isCustomerView = false }) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [items, setItems] = useState<MissingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<MissingItem | null>(null);

  useEffect(() => {
    loadMissingItems();
  }, [customerId, user]);

  const loadMissingItems = async () => {
    try {
      setLoading(true);
      let fetchedItems: MissingItem[] = [];
      
      if (isCustomerView && customerId) {
        fetchedItems = await missingItemService.getPendingMissingItems(customerId);
      } else if (user?.organizationId) {
        fetchedItems = await missingItemService.getMissingItemsByOrganization(user.organizationId);
      }
      
      setItems(fetchedItems);
    } catch (error) {
      console.error('Error loading missing items:', error);
      toast.error(t('common.errors.loadingFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsUploaded = (itemId: string) => {
    const item = items.find(i => i.id === itemId);
    if (item) {
      setSelectedItem(item);
      setUploadModalOpen(true);
    }
  };

  const handleUploadComplete = async (uploadedFileUrls: string[]) => {
    if (!selectedItem || !user) return;
    
    try {
      await missingItemService.markAsUploaded(selectedItem.id, user.id, uploadedFileUrls);
      toast.success(t('customerPortal.missingItems.markAsUploaded') + ' sikeres');
      setUploadModalOpen(false);
      setSelectedItem(null);
      loadMissingItems();
    } catch (error) {
      console.error('Error marking as uploaded:', error);
      toast.error(t('common.errors.saveFailed'));
    }
  };

  const handleApprove = async (itemId: string) => {
    if (!user) return;
    
    try {
      await missingItemService.approveMissingItem(itemId, user.id);
      toast.success(t('common.approved'));
      loadMissingItems();
    } catch (error) {
      console.error('Error approving item:', error);
      toast.error(t('common.errors.saveFailed'));
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      case 'high': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300';
      case 'medium': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'low': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'acknowledged': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case 'uploaded': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300';
      case 'approved': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'rejected': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <ClockIcon className="h-5 w-5" />;
      case 'acknowledged': return <CheckCircleIcon className="h-5 w-5" />;
      case 'uploaded': return <DocumentArrowUpIcon className="h-5 w-5" />;
      case 'approved': return <CheckCircleIcon className="h-5 w-5" />;
      case 'rejected': return <XCircleIcon className="h-5 w-5" />;
      default: return <ClockIcon className="h-5 w-5" />;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="text-center py-12">
        <CheckCircleIcon className="mx-auto h-12 w-12 text-green-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
          {t('customerPortal.missingItems.noItems')}
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {t('customerPortal.missingItems.description')}
        </p>
      </div>
    );
  }

  return (
    <>
      {selectedItem && (
        <MissingItemUploadModal
          isOpen={uploadModalOpen}
          onClose={() => {
            setUploadModalOpen(false);
            setSelectedItem(null);
          }}
          missingItemId={selectedItem.id}
          customerId={selectedItem.customerId}
          organizationId={selectedItem.organizationId}
          userId={user?.id || ''}
          userName={user?.displayName || ''}
          onUploadComplete={handleUploadComplete}
        />
      )}
      
      <div className="space-y-4">
      {items.map((item) => (
        <div
          key={item.id}
          className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:shadow-md transition-shadow"
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-2">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(item.priority)}`}>
                  {t(`customerPortal.missingItems.priority.${item.priority}`)}
                </span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1 ${getStatusColor(item.status)}`}>
                  {getStatusIcon(item.status)}
                  <span>{t(`customerPortal.missingItems.status.${item.status}`)}</span>
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {t(`customerPortal.missingItems.category.${item.category}`)}
                </span>
              </div>
              
              <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                {item.description}
              </h4>
              
              {item.notes && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  {item.notes}
                </p>
              )}
              
              <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
                <span>{t('customerPortal.missingItems.createdAt')}: {item.createdAt.toLocaleDateString('hu-HU')}</span>
                {item.dueDate && (
                  <span className="text-red-600 dark:text-red-400">
                    {t('customerPortal.missingItems.dueDate')}: {item.dueDate.toLocaleDateString('hu-HU')}
                  </span>
                )}
              </div>

              {/* Feltöltött fájlok megjelenítése */}
              {item.uploadedFiles && item.uploadedFiles.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Feltöltött fájlok ({item.uploadedFiles.length}):
                  </p>
                  <div className="space-y-1">
                    {item.uploadedFiles.map((fileUrl, index) => {
                      const fileName = fileUrl.split('/').pop()?.split('?')[0] || `Fájl ${index + 1}`;
                      const decodedFileName = decodeURIComponent(fileName.split('_').slice(1).join('_') || fileName);
                      return (
                        <a
                          key={index}
                          href={fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center space-x-2 text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:underline"
                        >
                          <DocumentArrowUpIcon className="h-4 w-4 flex-shrink-0" />
                          <span className="truncate">{decodedFileName}</span>
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            
            {isCustomerView && (item.status === 'pending' || item.status === 'acknowledged') && (
              <div className="ml-4">
                <button
                  onClick={() => handleMarkAsUploaded(item.id)}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors flex items-center space-x-2"
                >
                  <DocumentArrowUpIcon className="h-5 w-5" />
                  <span>{t('customerPortal.missingItems.uploadFiles')}</span>
                </button>
              </div>
            )}
            
            {!isCustomerView && item.status === 'uploaded' && (
              <div className="ml-4 flex flex-col space-y-2">
                <button
                  onClick={() => handleApprove(item.id)}
                  className="px-3 py-1 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700"
                >
                  {t('common.approve')}
                </button>
              </div>
            )}
          </div>
        </div>
      ))}
      </div>
    </>
  );
};

export default MissingItemsList;
